import { ComponentProps, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { trackLinkClick } from '../analytics';
import translate from '../functions/translate';
import Button from './button';
import Div from './div';
import { ReactComponent as Eye } from "../images/eye.svg";
import { ReactComponent as EyeSlash } from "../images/eye-slash.svg";
import { TextField } from '@cmsgov/ds-medicare-gov';

type TextFieldProps = ComponentProps<typeof TextField>;
type OptionalTextFieldProps = Partial<Pick<TextFieldProps, 'label' | 'name'>>
export type PasswordSectionProps = Pick<TextFieldProps,
    'errorPlacement' |
    'errorMessage' |
    'id' |
    'onBlur' |
    'onChange' |
    'inputRef' |
    'value' |
    'hint' |
    'labelClassName' |
    'fieldClassName'
> & OptionalTextFieldProps & {
    onFocus?: (...args: any[]) => any;
    onShowPasswordChange?: (showPassword: boolean) => void
    showForgotPasswordLink?: boolean
    showPassword?: boolean
}


const PasswordSection = (props: PasswordSectionProps) => {

    const [passwordShown, setPasswordShown] = useState(false);
    const [topOffset, setTopOffset] = useState(0);
    const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

    const { errorMessage, hint } = props;

    const calculateTopOffset = useCallback(() => {
        if (inputRef) {
            const containingDiv = inputRef.parentElement;
            if (containingDiv) {
                const { top: containerTop } = containingDiv.getBoundingClientRect();
                const { top: inputTop } = inputRef.getBoundingClientRect();
                const diff = inputTop - containerTop;
                setTopOffset(diff);
            }
        }
    }, [inputRef]);

    useEffect(() => {
        window.addEventListener('resize', calculateTopOffset);
        calculateTopOffset();

        return () => {
            window.removeEventListener('resize', calculateTopOffset);
        }
    }, [calculateTopOffset]);

    useEffect(() => {
        calculateTopOffset();
    }, [errorMessage, hint, calculateTopOffset]);

    const visibilityIsControlled = props.showPassword !== undefined;
    const togglePasswordVisiblity = () => {
        if (visibilityIsControlled && props.onShowPasswordChange) {
            props.onShowPasswordChange(!props.showPassword);
        } else {
            setPasswordShown(current => !current);
        }
    };

    const showPassword = visibilityIsControlled ? props.showPassword : passwordShown;

    interface TopOffset {
        top: number
    }
    interface BottomOffset {
        bottom: number
    }
    type TopOrBottomOffset = TopOffset | BottomOffset;

    type TopBottomOffset = [number, number];
    const buttonOffsets: TopBottomOffset = [topOffset, 12];
    const isTopError = props.errorPlacement === 'top';
    const getButtonOffset = (): TopOrBottomOffset => {
        const [top, bottom] = buttonOffsets;
        return isTopError ? { bottom } : { top: top + 10 };
    }

    return <>
        <Div isRow>
            <Div cols={12}>
                {/* .ds-c-label to make containing div the same width as the input to position the show/hide button */}
                <div className="ds-c-label ds-u-margin--0" style={{ position: 'relative' }}>
                    <TextField
                        fieldClassName={props.fieldClassName}
                        labelClassName={props.labelClassName}
                        errorPlacement={props.errorPlacement}
                        errorMessage={errorMessage}
                        id={props.id}
                        inputRef={ref => {
                            if (props.inputRef) {
                                props.inputRef(ref);
                            }
                            setInputRef(ref);
                        }}
                        label={props.label || 'Password'}
                        name={props.name || 'password'}
                        onBlur={props.onBlur}
                        onChange={props.onChange}
                        onFocus={props.onFocus}
                        type={showPassword ? "text" : "password"}
                        value={props.value}
                        hint={hint}
                    />
                    <Div
                        className='showHideToggle'
                        style={{
                            position: 'absolute',
                            right: 8,
                            ...getButtonOffset()
                        }}
                    >
                        {showPassword ? <EyeSlash/> : <Eye/>}
                        <Button
                            analyticsData={{
                                text: showPassword ? translate('hide') : translate('show'),
                                parentComponentHeading: translate('login')
                            }}
                            onClick={togglePasswordVisiblity}
                            variation={'ghost'}
                            style={{
                                textDecoration: 'none',
                                paddingRight: 3,
                                paddingLeft: 6
                            }}
                        >
                            {showPassword ? translate('hide') : translate('show')}
                            <span className={'ds-u-visibility--screen-reader'}> {translate('password')}</span>
                        </Button>
                    </Div>
                </div>

                {props.showForgotPasswordLink &&
                    <Link
                        to="/forgot-password"
                        onClick={trackLinkClick(translate('login'))}
                    >
                        {translate('forgot_password_question_mark')}
                    </Link>
                }
            </Div>
        </Div>
    </>
};

export default PasswordSection;
